import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } },
};
const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero({ booted, onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 4]);

  const copyCa = () =>
    toast("CONTRACT NOT LIVE YET", {
      description: "CA drops at launch — links revealed on X and Telegram first.",
    });

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-24"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-[-10%] h-[80vh] w-[60vw] -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(57,255,20,0.14),transparent)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[-20%] left-[-15%] h-[60vh] w-[50vw] rounded-full bg-[radial-gradient(closest-side,rgba(255,0,255,0.08),transparent)]"
      />

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr]">
        <motion.div
          variants={container}
          initial="hidden"
          animate={booted ? "show" : "hidden"}
        >
          <motion.p
            variants={fadeUp}
            className="animate-flicker mb-8 font-vt text-xl tracking-[0.15em] text-neon sm:text-2xl"
            data-testid="hero-eyebrow"
          >
            {"// warning: he turned around"}
          </motion.p>

          <h1 className="font-display leading-[0.95] font-black tracking-tighter uppercase">
            <span className="block overflow-hidden">
              <motion.span
                variants={lineReveal}
                className="glitch-hover block text-6xl text-[#EDEDE4] sm:text-7xl lg:text-[6.5rem]"
              >
                PEPE
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                variants={lineReveal}
                className="text-outline glitch-hover block text-6xl sm:text-7xl lg:text-[6.5rem]"
              >
                BUTT
              </motion.span>
            </span>
            <span className="block overflow-hidden pt-2">
              <motion.span
                variants={lineReveal}
                className="glow-neon block font-vt text-4xl font-normal tracking-[0.1em] text-neon sm:text-5xl lg:text-6xl"
              >
                $PEPEBUTT
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            className="mt-10 max-w-md text-sm leading-relaxed text-[#9fb39f] sm:text-base"
          >
            A frog. Two cheeks. Zero shame. The thiccest meme on Solana just
            turned around — say <span className="text-neon">gm</span> or look
            away forever.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-buy-button"
              onClick={() => onNavigate("#how-to-buy")}
              className="group flex items-center gap-3 border border-neon bg-neon px-7 py-4 font-mono text-sm font-bold tracking-[0.15em] text-nox uppercase transition-colors duration-200 hover:bg-transparent hover:text-neon"
            >
              <Sparkles size={16} className="transition-transform duration-300 group-hover:-rotate-12" />
              Buy $PEPEBUTT
            </button>
            <button
              data-testid="hero-copy-ca-button"
              onClick={copyCa}
              className="group flex items-center gap-3 border border-line px-7 py-4 font-mono text-sm tracking-[0.15em] text-[#9fb39f] uppercase transition-colors duration-200 hover:border-neon hover:text-neon"
            >
              <Copy size={15} />
              CA: TBA
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={booted ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.55, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-md"
        >
          <motion.div style={{ y: imgY, rotate: imgRotate }} className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-8 rounded-full bg-[radial-gradient(closest-side,rgba(57,255,20,0.18),transparent)]"
            />
            <div className="img-scan relative border border-neon/40">
              <img
                src="/assets/pepebutt.png"
                alt="Pepe Butt — a smug green frog turned around, cheeks out"
                data-testid="hero-cat-image"
                className="img-glitch block w-full"
              />
              <span aria-hidden="true" className="absolute top-0 left-0 h-5 w-5 border-t-2 border-l-2 border-neon" />
              <span aria-hidden="true" className="absolute top-0 right-0 h-5 w-5 border-t-2 border-r-2 border-neon" />
              <span aria-hidden="true" className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-neon" />
              <span aria-hidden="true" className="absolute right-0 bottom-0 h-5 w-5 border-r-2 border-b-2 border-neon" />
            </div>
            <div className="absolute -bottom-4 left-4 border border-neon bg-nox px-4 py-2">
              <span className="font-vt text-xl text-neon">CERTIFIED: THICC</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={booted ? { opacity: 1 } : {}}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="mt-20 border-t border-line"
        data-testid="hero-status-bar"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-8 gap-y-2 px-5 py-4 font-mono text-[11px] tracking-[0.2em] text-[#6b7f6b] uppercase sm:px-8">
          <span>CHAIN: SOLANA</span>
          <span>TAX: 0/0</span>
          <span>LP: BURNED</span>
          <span className="animate-flicker text-neon">STATUS: CLAPPING</span>
        </div>
      </motion.div>
    </section>
  );
}
